import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl, getLocale } from 'umi';
import moment from 'moment';
import {
  formatNum,
  formatPercentNum,
  handleDownload,
  formatExeclNum,
  formatExeclDecimal,
  formatExeclNormalPercent,
  getIsErrorValue
} from '@/pages/dashboard/utils/utils';
import useOverviewLocalesIntl from '@/pages/dashboard/pages/overview/_config/useOverviewLocalesIntl';
import { EngagementRevenueMetricsItemProps } from '@/pages/dashboard/pages/overview/_config/interface';
import { useSelectorMap, useWeekDay } from '@/pages/dashboard/utils/customHook';
import { TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from '@/pages/home/_config/config';
import { cloneDeep } from 'lodash-es';

const DATE_COLUMN_WIDTH = 120;
const METRIC_COLUMN_WIDTH = 90;

const useIndex = (props: EngagementRevenueMetricsItemProps) => {
  const {
    defaultTab,
    selected,
    defaultTabText,
    loading: loadingProps,
    defaultData,
    excelFilterName,
    tabList,
    update,
    type = 'DAY',
    tabConfig = {},
    intList = [],
    percentList = [],
    isMoblie = false,
    showLastMonthYear = false,
    extraMetrics = [],
    visibleLegends,
    legends,
  } = props;
  const { getWeekDayStr, getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl(); // 中英切换方法
  const { getSelectorText } = useSelectorMap();
  const {
    CHART_TYPE,
    LINE_METRICS_LEGEND_MAP,
    PC_LINE_METRICS_LEGEND_MAP,
    PC_LEGEND_LAST_WM,
  } = useOverviewLocalesIntl(extraMetrics, {
    visibleLegends
  });
  let LEGEND_MAP;

  if (isMoblie) {
    LEGEND_MAP = LINE_METRICS_LEGEND_MAP;
  } else if (showLastMonthYear) {
    LEGEND_MAP = PC_LEGEND_LAST_WM;
  } else {
    LEGEND_MAP = PC_LINE_METRICS_LEGEND_MAP;
  }
  

  const [data, setData] = useState({
    [CHART_TYPE.COLUMN]: {
      series: [],
      xAxis: [],
      legend: [],
    },
    [CHART_TYPE.LINE]: [],
    [CHART_TYPE.TABLE]: [],
  } as {
    [key: string]: any;
  });

  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState(CHART_TYPE.LINE);
  const [dataType, setDataType] = useState(defaultTab);
  const [selectedValue, setSelectedValue] = useState(selected);
  const [dataTypeText, setDataTypeText] = useState(defaultTabText);

  const userQuota = [...intList, 'dau', 'wau', 'mau', 'new_users', 'active_users', 'total_users', 'all_entitlement',
    'new', 'tru', 'pcu', 'acu', 'payer', 'avg.wau', 'avg.mau', 'units_number', 'mtx_number', 'mtx_payers'];
  const hasPercentList = [...percentList,
    'd1', 'd2', 'd3', 'd7', 'weighted_d2', 'weighted_d3', 'weighted_d7',
    'w2', 'w3', 'weighted_w2', 'weighted_w3',
    'm2', 'm3', 'weighted_m2', 'weighted_m3',
    'payer_ratio', 'payer_rate', 'mtx_payer_rate'
  ];

  const metricConfig = useMemo(() => tabConfig?.find?.((item: any) => item.value === dataType) || {}, [tabConfig, dataType]);

  const { text } = metricConfig;

  // 更新数据
  const updateData = (res: any[]) => {
    let chartOption: any = [];
    let columnData: any = [];
    let lineData: any = [];
    let tableData: any = [];
    if (selectedValue && selectedValue !== 'accumulated') {
      chartOption = res || [];
      lineData = changeDataByDimension(chartOption, CHART_TYPE.LINE);
      columnData = changeDataByDimension(chartOption, CHART_TYPE.COLUMN);
      tableData = changeDataByDimension(chartOption, CHART_TYPE.TABLE);
    } else {
      chartOption = res?.map((item) => {
        const items = { date: item?.date, ...item?.metrics };
        return ({
          data: items[dataType.replace('.', '_')],
          date: items.date,
        });
      });
      columnData = changeDataByChartType(chartOption, CHART_TYPE.COLUMN);
      lineData = changeDataByChartType(chartOption, CHART_TYPE.LINE);
      tableData = changeDataByChartType(chartOption, CHART_TYPE.TABLE);
    }
    setData({
      ...data,
      [CHART_TYPE.COLUMN]: columnData,
      [CHART_TYPE.LINE]: lineData,
      [CHART_TYPE.TABLE]: tableData,
    } as any);
  };

  const onChartTypeChange = (chartType: { id: React.SetStateAction<string>; }) => {
    setChartType(chartType.id);
  };

  // data type change
  const onDataTypeChange = (dataType: string, text: React.SetStateAction<string>, selected: any) => {
    update?.(dataType, selected);
    setSelectedValue(selected);
    setDataType(dataType);
    setDataTypeText(text);
  };

  // 时间处理
  const handleDate = (date: moment.MomentInput) => (type === 'MONTH' ? moment(date).format("YYYY-MM") : moment(date).format("YYYY-MM-DD"));

  // 根据图表类型改变数据格式
  const changeDataByChartType = (chartOption: any, chartType: string) => {
    // 根据不同框架 不同时间粒度展示不同图例
    const legendObj = LEGEND_MAP?.[type] || {};
    
    // 直接使用从 LEGEND_MAP 中获取的名称（已经通过 extraMetrics 处理过）
    const legendArr: any = Object.keys(legendObj).map(key => {
      const item = legendObj[key];
      return {
        ...item,
        name: item.name,
      };
    });
    
    const legend: any = [];
    const legendKey: any = [];
    const selectedLegend: Record<string, boolean> = {};
    legendArr?.map((item: any) => {
      const isVisibleLegend = visibleLegends && visibleLegends.length > 0;
      const matchedVisibleLegend = isVisibleLegend
        ? visibleLegends.find((vl) => vl.key === item?.key)
        : undefined;
      const legendName = isVisibleLegend
        ? matchedVisibleLegend?.showMetricsName && item?.name
          ? `${text} (${item?.name})`
          : item?.name || text || ''
        : item?.name
          ? `${text} (${item?.name})`
          : text || '';
      legend?.push(legendName);
      legendKey?.push(item?.key);
      if (item?.selected === false) {
        selectedLegend[legendName] = false;
      }
    });
    const chartMap: any = {
      [CHART_TYPE.COLUMN]: () => {
        const yAxis: any = {};
        // 横坐标
        const xAxis: string[] = [];
        // 数据配置
        const seriesBase = {
          type: 'bar',
          barMaxWidth: 80,
          cursor: 'pointer',
        };
        const data: {
          [key: string]: string[];
        } = {};
        legendKey?.map((item: any) => data[item] = []);
        chartOption?.forEach?.((item: any) => {
          const date = handleDate(item?.date);
          legendKey?.forEach((dataKey: any) => {
            const value = item?.data?.[dataKey];
            data?.[dataKey]?.push(value);
          });
          xAxis?.push(date);
        });

        const series = legend?.map((item: any, index: number) => {
          const dataKey = legendKey?.[index];
          return {
            ...seriesBase,
            name: item,
            data: data[dataKey],
            z: legend?.length - index
          };
        });

        return {
          series,
          yAxis,
          xAxis,
          legend,
          selectedLegend,
        };

      },
      [CHART_TYPE.LINE]: () => {
        const data = chartMap[CHART_TYPE.COLUMN]();
        return {
          ...data,
          series: data?.series?.map((item: any) => ({
            ...item,
            type: 'line',
          })),
        };
      },
      [CHART_TYPE.TABLE]: () => {
        const unit = tabConfig?.find((item: { value: string; }) => item.value === dataType)?.unit;
        const tableOption = legendArr?.map((item: any) => {
          const dataKey = item?.key || '';
          const legendName = item?.name ? `${text} (${item?.name})` : text;
          return {
            key: dataKey,
            align: 'right',
            width: METRIC_COLUMN_WIDTH + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
            header: legendName,
            render: (cvm: { [x: string]: string | number; }) => {
              const _val = hasPercentList?.includes(dataType) ?
                (dataType === 'accumulated_refund_rate' || dataType === 'lifetime_refund_rate') && cvm[dataKey] < 0 ? `-${  formatPercentNum(parseFloat(cvm[dataKey]))}`
                  : formatPercentNum(parseFloat(cvm[dataKey]))
                : formatNum(cvm[dataKey], false, userQuota.includes(dataType));
              return (<span>{unit && _val !== '-' ? unit + _val : _val}</span>);
            },
          };
        }) || [];
        const option = [
          {
            key: 'date',
            align: 'left',
            fixed: 'left',
            width: DATE_COLUMN_WIDTH + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
            header: formatMessage({ id: 'overview_table_date' }),
            render: (cvm: { date: moment.MomentInput; }) => {
              const weekDay = getWeekDay(cvm?.date);
              const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
              return <>
                <span className='table-text'>{handleDate(cvm?.date)}</span>
                {type?.toLowerCase() !== 'month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
              </>;
            },
          },
          ...tableOption
        ];
        const data = chartOption?.map((item: { date: any; data: { [x: string]: any; }; }) => {
          const { date, data } = item || {};
          return {
            date,
            ...data,
          };
        });

        return {
          option,
          data
        };
      },
    };

    return chartMap[chartType]?.();
  };

  // 获取维度值(支持标准维度和自定义维度)
  const getDimensionValue = (item, dimensionKey: string, options?: {
    getKey: boolean;
  }) => {
    const { getKey } = options || {};
    // 检查是否有 custom_dimensions 数组
    if (item?.custom_dimensions && Array.isArray(item.custom_dimensions)) {
      const customDim = item?.custom_dimensions?.find((dim) => dim?.key === dimensionKey);
      if (customDim) {
        return customDim.value ?? '';
      }
    }

    const dimKey = item[dimensionKey] ?? '';
    const dimName = item[`${dimensionKey}_name`];
    // 回退到标准维度字段
    return getKey ? dimKey : dimName || dimKey;
  };

  // 根据维度改变数据格式
  const changeDataByDimension = (chartOption, chartType: string) => {
    const uniqueDims: Array<{ raw: string; display: string; }> = chartOption?.reduce(
      (acc: Array<{ raw: string; display: string; }>, item) => {
        const raw = getDimensionValue(item, selectedValue, { getKey: true, }) ?? '';
        if (!acc.find((d) => d.raw === raw)) {
          acc.push({ raw, display: getDimensionValue(item, selectedValue) ?? '' });
        }
        return acc;
      },
      []
    ) ?? [];

    if (legends && legends.length > 0) {
      uniqueDims.sort((a, b) => {
        const idxA = legends.indexOf(a.raw);
        const idxB = legends.indexOf(b.raw);
        return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB);
      });
    }

    const legend: string[] = uniqueDims.map(({ display }) => getSelectorText(selectedValue, display) ?? '');

    const xAxis: any = [...new Set(chartOption?.map((item: { date: moment.MomentInput; }) => handleDate(item?.date) ?? ''))];
    const yAxis: any = {};
    const seriesBase = {
      type: chartType === 'line' ? 'line' : 'bar',
      barMaxWidth: 80,
      barCategoryGap: '50%',
      cursor: 'pointer',
    };
    const series = legend?.map((item: any) => {
      const data: any = [];
      chartOption?.map((cItem: any) => {
        const dimValue = getDimensionValue(cItem, selectedValue);
        if (item === getSelectorText(selectedValue, dimValue)) {
          data.push(cItem?.metrics[dataType]?.value ?? '');
        }
      });
      if (chartType === 'column') return { ...seriesBase, name: item, data, stack: 1, };
      return { ...seriesBase, name: item, data };
    });

    const chartMap = {
      [CHART_TYPE.COLUMN]: () => ({ legend, xAxis, yAxis, series }),
      [CHART_TYPE.LINE]: () => ({ legend, xAxis, yAxis, series }),
      [CHART_TYPE.TABLE]: () => {
        const unit = tabConfig?.find((item: { value: string; }) => item.value === dataType)?.unit;
        const data: any = [];
        xAxis?.map((item: any, index: string | number) => {
          const obj = { date: item ?? '' };
          series?.map((v: { data: { [x: string]: any; }; name: string | number; }) => {
            const val = v?.data[index];
            obj[v?.name] = val ?? '';
          });
          data.push(obj);
        });

        const metricOption = legend?.map((item: string) => ({
          key: item,
          align: 'right',
          width: METRIC_COLUMN_WIDTH + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
          header: getSelectorText(selectedValue, item),
          render: (cvm: { [x: string]: string | number; }) => {
            const _val = hasPercentList?.includes(dataType) ?
              (dataType === 'accumulated_refund_rate' || dataType === 'lifetime_refund_rate') && cvm[item] < 0 ? `-${  formatPercentNum(parseFloat(cvm[item]))}`
                : formatPercentNum(parseFloat(cvm[item]))
              : formatNum(cvm[item], false, userQuota.includes(dataType));
            return (<span>{unit && _val !== '-' ? unit + _val : _val}</span>);
          },
        })
        );
        const option = [
          {
            key: 'date',
            align: 'left',
            fixed: 'left',
            width: DATE_COLUMN_WIDTH + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
            header: formatMessage({ id: 'overview_table_date' }),
            render: (cvm: { date: moment.MomentInput; }) => {
              const weekDay = getWeekDay(cvm?.date);
              const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
              return <>
                <span className='table-text'>{handleDate(cvm?.date)}</span>
                {type?.toLowerCase() !== 'month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
              </>;
            },
          },
          ...metricOption
        ];
        return {
          option,
          data
        };
      },
    };
    return chartMap[chartType]?.();
  };

  // 下载文件
  const downloadData = useCallback(() => {
    const filterName = `${excelFilterName  }_${  dataTypeText}`;
    const dataArr = cloneDeep(data?.[CHART_TYPE.TABLE]?.data) || [];
    const legendObj = LEGEND_MAP?.[type] || {};
    const donwLoadData = dataArr?.map((item: { [x: string]: string; date?: any; }) => {
      if (selectedValue !== 'accumulated') {
        Object.keys(item)?.map(v => {
          if (v === 'date') {
            item[v] = getIsErrorValue(item[v]) ? '-' : handleDate(item[v]);
          } else {
            const itemsObj = hasPercentList?.includes(dataType) ? formatExeclNormalPercent(item[v]) : userQuota.includes(dataType) ? formatExeclNum(item[v]) : formatExeclDecimal(item[v]);
            item[v] = itemsObj;
          }
        });
        return item;
      } 
      const formattedValues: any = {};
      for (const key in legendObj) {
        if (legendObj.hasOwnProperty(key)) {
          const dataKey = legendObj?.[key]?.key || '';
          const value = item?.[dataKey] ?? '';
          formattedValues[dataKey] = hasPercentList?.includes(dataType) ? formatExeclNormalPercent(value) : userQuota?.includes(dataType) ? formatExeclNum(value) : formatExeclDecimal(value);
        }
      }
      return {
        ...item,
        date: item?.date ? handleDate(item?.date) : '-',
        ...formattedValues,
      };
      

    }) || [];
    const tableConfig = {
      option: data?.[CHART_TYPE.TABLE]?.option,
      downloadData: donwLoadData
    };
    handleDownload(tableConfig, filterName);
  }, [data, dataTypeText]);

  useEffect(() => {
    setDataType(defaultTab);
    setDataTypeText(defaultTabText);
  }, [defaultTab]);

  useEffect(() => {
    setSelectedValue(selected);
  }, [selected]);

  // 父级控制 loading
  useEffect(() => {
    setLoading(loadingProps);
  }, [loadingProps]);


  // 父级控制 data
  useEffect(() => {
    updateData(defaultData);
  }, [defaultData, getLocale(), dataType]);

  useEffect(() => {
    const newDataTypeText = tabList?.find?.((item: { value: string; }) => item.value === dataType)?.text;
    setDataTypeText(newDataTypeText);
  }, [tabList]);

  return {
    data,
    loading,
    dataType,
    selected: selectedValue,
    chartType,
    userQuota,
    hasPercentList,
    metricConfig,
    getWeekDayStr,
    downloadData,
    onDataTypeChange,
    onChartTypeChange,
    LEGEND_MAP, // 导出 LEGEND_MAP 供 chartItem 使用
    dataTypeText, // 导出 dataTypeText 供 chartItem 使用
  };
};

export default useIndex;
